import { cn } from "@tcg/ui";
import { CheckCircle2, CircleDashed } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { PickupLine } from "@/lib/types";

/**
 * The cards of the web order (T07-29): each card that must be scanned shows ✓ once its slab /
 * label was scanned — 「お渡し完了」 opens only when every one is ✓ (the server checks again).
 */
export function VerifyLines({
  lines,
  verified,
}: {
  lines: PickupLine[];
  /** lineId → the code that matched it */
  verified: ReadonlyMap<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col gap-2" aria-label={t("pickup.items")}>
      {lines.map((line) => {
        const ok = !line.needsScan || verified.has(line.lineId);
        return (
          <li
            key={line.lineId}
            data-testid="pickup-line"
            data-verified={ok}
            className={cn(
              "flex min-h-pos-touch items-center gap-3 rounded-md border px-3 py-2",
              line.needsScan && ok && "border-pos-ok bg-pos-ok/10",
            )}
          >
            {ok ? (
              <CheckCircle2 aria-hidden className="size-7 shrink-0 text-pos-ok" />
            ) : (
              <CircleDashed aria-hidden className="size-7 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-medium">{line.title}</p>
              <p className="text-sm text-muted-foreground">
                {line.conditionLabel ? `${line.conditionLabel} · ` : ""}
                {t("pickup.code", { code: line.sku })}
                {line.quantity > 1 ? ` · ×${String(line.quantity)}` : ""}
              </p>
            </div>
            <span className="text-sm font-medium">
              {!line.needsScan
                ? t("pickup.no_scan_needed")
                : ok
                  ? t("pickup.checked")
                  : t("pickup.scan_this")}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
